/**
 * 前端防爬辅助：反自动化检测 + 行为追踪
 * 结果随请求头 X-Anti-Crawler 带给后端（base64url 编码的 JSON），供限流/风控使用；不阻断前端逻辑。
 *
 * 后端解析示例（Node 16+）：
 *   const payload = JSON.parse(Buffer.from(req.headers['x-anti-crawler'] || '', 'base64url').toString('utf8'))
 *   // payload.suspicious, payload.reasons[], payload.behavior: { hasScrolled, hasMovedMouse, hasClicked, dwellTimeMs, pageEnteredAt, lastActivityAt }
 */

// ---------- 1. 反自动化检测 ----------
function detectAutomation() {
  const reasons = []
  if (typeof navigator === 'undefined') {
    return { suspicious: true, reasons: ['no-navigator'] }
  }
  if (navigator.webdriver === true) {
    reasons.push('webdriver')
  }
  if (navigator.plugins && navigator.plugins.length === 0) {
    reasons.push('no-plugins')
  }
  if (navigator.languages && navigator.languages.length === 0) {
    reasons.push('no-languages')
  }
  const ua = navigator.userAgent || ''
  if (/HeadlessChrome|PhantomJS|Selenium|Puppeteer|Playwright/i.test(ua)) {
    reasons.push('headless-ua')
  }
  // 已移除 chrome-no-runtime：普通 Chrome 下也常无 chrome.runtime，误报多
  if (typeof window !== 'undefined' && window.outerWidth === 0 && window.outerHeight === 0) {
    reasons.push('zero-outer-size')
  }
  return {
    suspicious: reasons.length > 0,
    reasons
  }
}

// ---------- 2. 行为追踪 ----------
const behaviorState = {
  hasScrolled: false,
  hasMovedMouse: false,
  hasClicked: false,
  firstActivityAt: null,
  lastActivityAt: null,
  pageEnteredAt: typeof Date !== 'undefined' ? Date.now() : 0
}

let behaviorInited = false

function throttle(fn, ms) {
  let last = 0
  return function _throttled() {
    const now = Date.now()
    if (now - last >= ms) {
      last = now
      fn()
    }
  }
}

function updateActivity(key) {
  behaviorState[key] = true
  const now = Date.now()
  if (!behaviorState.firstActivityAt) {
    behaviorState.firstActivityAt = now
  }
  behaviorState.lastActivityAt = now
}

function initBehaviorTracking() {
  if (behaviorInited || typeof window === 'undefined') return
  behaviorInited = true
  behaviorState.pageEnteredAt = Date.now()

  window.addEventListener('scroll', () => updateActivity('hasScrolled'), { passive: true, capture: true })
  window.addEventListener('mousemove', throttle(() => updateActivity('hasMovedMouse'), 400), { passive: true })
  window.addEventListener('click', () => updateActivity('hasClicked'), { capture: true })
}

/**
 * 标记「当前页」进入时间，用于计算 dwellTimeMs（当前页停留时长）。
 * 建议在路由每次进入时调用（含首页、详情、阅读页等），这样 dwellTimeMs 不限于漫画阅读场景。
 */
function markPageEntered() {
  const now = typeof Date !== 'undefined' ? Date.now() : 0
  behaviorState.pageEnteredAt = now
}

function getBehavior() {
  const now = typeof Date !== 'undefined' ? Date.now() : 0
  // dwellTimeMs：从进入当前页到当前的时长（毫秒）。需在每次路由进入时调用 markPageEntered()，覆盖首页/详情/阅读等所有场景
  const dwellTimeMs = behaviorState.pageEnteredAt ? Math.max(0, now - behaviorState.pageEnteredAt) : 0
  return {
    hasScrolled: behaviorState.hasScrolled,
    hasMovedMouse: behaviorState.hasMovedMouse,
    hasClicked: behaviorState.hasClicked,
    dwellTimeMs,
    pageEnteredAt: behaviorState.pageEnteredAt,
    lastActivityAt: behaviorState.lastActivityAt
  }
}

/**
 * 获取当前检测 + 行为数据，供随请求上报
 * @returns {{ suspicious: boolean, reasons: string[], behavior: object }}
 */
function getRequestPayload() {
  const detection = detectAutomation()
  const behavior = getBehavior()
  return {
    suspicious: detection.suspicious,
    reasons: detection.reasons,
    behavior
  }
}

/**
 * 将 payload 编码为请求头可用的短字符串（base64url）
 * 后端解析：JSON.parse(atob(headerValue.replace(/-/g,'+').replace(/_/g,'/')))
 * @returns {string}
 */
function encodePayloadForHeader() {
  try {
    const payload = getRequestPayload()
    const json = JSON.stringify(payload)
    const base64 = typeof btoa !== 'undefined' ? btoa(unescape(encodeURIComponent(json))) : ''
    return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
  } catch (e) {
    return ''
  }
}

export {
  detectAutomation,
  getBehavior,
  getRequestPayload,
  encodePayloadForHeader,
  initBehaviorTracking,
  markPageEntered
}
